import type { ServerStat } from "@/api/nzb";
import { Status } from "@/components/ui/Status";
import { cn } from "@/lib/cn";

/**
 * Renders a row of chips, one per news server that contributed
 * articles to a download. Critical visibility for multi-server
 * deployments — without it, the user can't tell whether a Severance-
 * class release was rescued by their fallback Eweka or whether
 * Newshosting carried it solo.
 *
 * Visual rules:
 *   - 0 servers → render nothing (we haven't pulled an article yet, or
 *     the engine didn't track per-server stats for legacy rows).
 *   - 1 server → single neutral chip with the name. Implies "primary
 *     delivered the whole job, no fallback needed".
 *   - 2+ servers → one chip per server. Sorted by bytes contributed
 *     (largest first) so the dominant carrier is read first. Chips
 *     show byte share when ≥ 2 contributors; otherwise just the name.
 *
 * The chip shows article-failure count via a "•N" suffix when any
 * articles failed at that server (cascade signal: "this server tried
 * but missed N, and another server filled the gap").
 */
export function ServerChips({
  servers,
  className,
}: {
  servers: ServerStat[];
  className?: string;
}) {
  if (servers.length === 0) return null;

  // Sort: largest byte contribution first.
  const sorted = [...servers].sort(
    (a, b) => b.bytes_downloaded - a.bytes_downloaded,
  );
  const total = sorted.reduce((acc, s) => acc + s.bytes_downloaded, 0);
  const multi = sorted.length > 1;

  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {sorted.map((s) => {
        const pct =
          multi && total > 0
            ? Math.round((s.bytes_downloaded / total) * 100)
            : null;
        return (
          <Status
            key={s.server_id}
            variant="chip"
            tone={s.articles_failed > 0 ? "warn" : "neutral"}
          >
            <span className="font-mono">{s.server_name}</span>
            {pct !== null && (
              <span className="ml-1 font-mono text-foreground/55">
                {pct}%
              </span>
            )}
            {s.articles_failed > 0 && (
              <span
                className="ml-1 font-mono text-warn"
                title={`${s.articles_failed} articles failed at this server`}
              >
                ·{s.articles_failed}
              </span>
            )}
          </Status>
        );
      })}
    </div>
  );
}
